import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  getDashboardLink,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
  AnalysisViewFragment,
  DashboardAnalysisMetadataInput,
} from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';
import { FeedbackSwitchIcon } from './FeedbackSwitchIcon';
import { WisdomSwitchIcon } from './WisdomSwitchIcon';

interface SwitchFeedAnalysisViewMenuItemProps {
  analysis: AnalysisViewFragment;
  closeMenu: () => void;
}

export const SwitchFeedAnalysisViewMenuItem = ({
  analysis,
  closeMenu,
}: SwitchFeedAnalysisViewMenuItemProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { showSuccessNotification } = useNotificationContext();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const dashboardAnalysesMetadata = useDashboardStore(
    ({ metadata }) => metadata.analysesMetadata,
  );
  const shouldShowAsFeed = useDashboardStore(
    ({ metadata }) =>
      metadata.analysesMetadata.find((metadata) => metadata.id === analysis.id)
        ?.shouldShowAsFeed,
  );
  const currentAnalysisMetadata = dashboardAnalysesMetadata.find(
    (metadata) => metadata.id === analysis.id,
  );
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { upsertDashboard } = useUpsertDashboard();

  const getUpdatedAnalysesMetadata = (): DashboardAnalysisMetadataInput[] => {
    return Boolean(currentAnalysisMetadata)
      ? dashboardAnalysesMetadata.map((metadata) =>
          metadata.id === analysis.id
            ? {
                ...metadata,
                shouldShowAsFeed: !shouldShowAsFeed,
              }
            : metadata,
        )
      : [
          ...dashboardAnalysesMetadata,
          {
            id: analysis.id,
            shouldShowAsFeed: !shouldShowAsFeed,
          },
        ];
  };

  const handleSwitchView = async () => {
    const updatedAnalysesMetadata = getUpdatedAnalysesMetadata();

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: {
        analysesMetadata: updatedAnalysesMetadata,
      },
    });

    closeMenu();

    if (dashboard?.editToken) {
      await upsertDashboard({
        dashboardProps: {
          metadataV2: {
            analysesMetadata: updatedAnalysesMetadata,
          },
        },
      });
      showSuccessNotification({
        contentComponent: 'Your changes have been saved successfully.',
        props: {},
      });
    }

    trackDashboardEvent({
      eventName: !shouldShowAsFeed
        ? 'Dashboard - Toggled To Feedback List View'
        : 'Dashboard - Toggled To Prompt Answer View',
      dashboardLink: dashboard ? getDashboardLink(dashboard, tenantSlug) : '',
      analysisLink: getAnalysisURL(analysis, tenantSlug),
      isFeedbackListView: !shouldShowAsFeed ? true : undefined,
    });
  };

  return (
    <MenuItem onClick={handleSwitchView}>
      <ListItemIcon>
        {!shouldShowAsFeed ? <FeedbackSwitchIcon /> : <WisdomSwitchIcon />}
      </ListItemIcon>
      <ListItemText>
        Switch to {!shouldShowAsFeed ? 'feedback list' : 'prompt answer'} view
      </ListItemText>
    </MenuItem>
  );
};
